import { FC } from "react";
import {
  Geozone,
  Geozones,
  GoogleMap,
  Marker,
  MarkerInfoWindow,
  Markers,
  MarkersClusterer,
  MarkersClustererInfoWindow,
  MarkerTooltip,
  TopLeftSection,
  TopRightSection,
  TopSection,
  types,
  utils
} from "@vilocnv/allsetra-core";
import useDashboardMapPage from "./useDashboardMapPage";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DashboardMapActionsButtons from "./DashboardMapActionsButtons";
import DashboardMapFilter from "./DashboardMapFilter";
import { useTheme } from "@mui/material";

const Map: FC = () => {
  const { t } = useTranslation(["translation", "formFieldsTranslation"]);
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    objectFilters,
    markers,
    showClusters,
    showSatelliteMode,
    showTraffic,
    geozones,
    showGeozones,
    isLoadingObjects,
    isLoadingObjectDetail,
    groups,
    isLoadingGroups,
    keys,
    isLoadingKeys,
    objectTypes,
    isLoadingObjectTypes,
    selectedTooltipMarker,
    selectedObject,
    selectedCluster,
    modalFilterOpen,
    handleMapLoad,
    handleMarkerMouseOver,
    handleMarkerMouseOut,
    handleShowInfoWindowClick,
    handleCloseInfoWindowClick,
    handleClusterClick,
    handleClusterCloseInfoWindowClick,
    handleShowSatelliteButtonClick,
    handleShowClustersButtonClick,
    handleShowTrafficButtonClick,
    handleShowGeozonesButtonClick,
    handleFilterModalCloseClick,
    handleFilterModalOpenClick,
    handleModalFilterFormSubmit,
    handleFilterChange,
    isSelectOpen,
    setIsSelectOpen,
    pzSwitchMetadata
  } = useDashboardMapPage();

  return (
    <GoogleMap
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY!}
      onLoad={handleMapLoad}
      showSatelliteMode={showSatelliteMode}
      showTraffic={showTraffic}
      isLoading={isLoadingObjects}
      zoom={7}
      options={{
        disableDefaultUI: false,
        fullscreenControl: false,
        mapTypeControl: false,
        zoomControl: false
      }}
    >
      <TopSection>
        <TopLeftSection>
          <DashboardMapFilter
            markers={markers}
            isFilterModalOpen={modalFilterOpen}
            onFilterModalCloseClick={handleFilterModalCloseClick}
            onFilterModalOpenClick={handleFilterModalOpenClick}
            onModalFormSubmit={handleModalFilterFormSubmit}
            group={groups}
            isLoadingGroups={isLoadingGroups}
            objectTypes={objectTypes}
            isLoadingObjectTypes={isLoadingObjectTypes}
            activeAccountKeys={keys}
            isLoadingActiveAccountKeys={isLoadingKeys}
            mapFilters={objectFilters}
            onFilterChange={handleFilterChange}
            theme={theme}
            t={t}
            setIsSelectOpen={setIsSelectOpen}
            isSelectOpen={isSelectOpen}
          />
        </TopLeftSection>
        <TopRightSection>
          <DashboardMapActionsButtons
            showClusters={showClusters}
            onShowClustersButtonClick={handleShowClustersButtonClick}
            showSatelliteMode={showSatelliteMode}
            onShowSatelliteButtonClick={handleShowSatelliteButtonClick}
            showTraffic={showTraffic}
            onShowTrafficButtonClick={handleShowTrafficButtonClick}
            showGeozones={showGeozones}
            onShowGeozonesButtonClick={handleShowGeozonesButtonClick}
          />
        </TopRightSection>
      </TopSection>
      <Markers markers={markers} showClusters={showClusters}>
        {(marker: types.MarkerType) => {
          return (
            <Marker
              uniqueId={marker.uniqueId}
              position={marker.position}
              icon={utils.toGoogleMapsIcon(marker.icon)}
              onMouseOver={() => handleMarkerMouseOver(marker.uniqueId)}
              onMouseOut={() => handleMarkerMouseOut()}
              onClick={() => handleShowInfoWindowClick(marker.uniqueId)}
            />
          );
        }}
      </Markers>
      {selectedTooltipMarker && (
        <MarkerTooltip
          position={selectedTooltipMarker.position}
          label={selectedTooltipMarker.name}
        />
      )}
      {selectedObject && (
        <MarkerInfoWindow
          objectData={selectedObject}
          position={{
            lat: selectedObject?.location?.latitude ?? 0,
            lng: selectedObject?.location?.longitude ?? 0
          }}
          onCloseClick={() => handleCloseInfoWindowClick()}
          isLoadingLocation={isLoadingObjectDetail}
          isLoadingMetaData={isLoadingObjectDetail}
          translatorFn={t}
          navigateFn={navigate}
          pzSwitchMetadata={pzSwitchMetadata}
        />
      )}
      <MarkersClusterer
        markers={markers}
        showClusters={showClusters}
        onClick={handleClusterClick}
      >
        {(marker, clusterer) => (
          <Marker
            key={marker.uniqueId}
            uniqueId={marker.uniqueId}
            position={marker.position}
            icon={marker.icon}
            onMouseOver={() => handleMarkerMouseOver(marker.uniqueId)}
            onMouseOut={() => handleMarkerMouseOut()}
            onClick={() => handleShowInfoWindowClick(marker.uniqueId)}
            clusterer={clusterer}
            noClustererRedraw={true}
          />
        )}
      </MarkersClusterer>
      {selectedCluster && (
        <MarkersClustererInfoWindow
          cluster={selectedCluster}
          markers={markers}
          onClose={handleClusterCloseInfoWindowClick}
          translatorFn={t}
          navigateFn={navigate}
        />
      )}
      <Geozones geozones={geozones} showGeozones={showGeozones}>
        {(geozone) => <Geozone geozone={geozone} />}
      </Geozones>
    </GoogleMap>
  );
};

export default Map;
